import { useCallback, useMemo } from 'react';
import Cookies from '@comeon/tomcat-cookies';
import useFullLogin from '~/components/authentication/login/use-full-login.js';
import useConfiguration from '../configuration/use-configuration.js';
import useGuestLogin from '../components/guest-login/hooks/use-guest-login';
import useExternalLogin from '~/components/guest-login/hooks/use-external-login';

/**
 * Initialize a login flow
 *
 * @example
 * ...
 * const { initializeLogin } = useLoginFlow();
 * ...
 * initializeLogin();
 * ...
 *
 */
const useLoginFlow = () => {
	const { useTrustlyLogin, showLoginAsGuestOption, isAnonymous } = useConfiguration();
	const { redirectToExternalLogin } = useExternalLogin();
	const { redirectToGuestLogin } = useGuestLogin();
	const { redirectToLogin } = useFullLogin();

	const isTrustlyLoginFlowEnabled = useMemo(
		() => useTrustlyLogin && !showLoginAsGuestOption,
		[useTrustlyLogin, showLoginAsGuestOption]
	);

	const isDepositFlowEnabled = useMemo(() => isAnonymous, [isAnonymous]);

	/**
	 * Initialize a login flow
	 *
	 * from is an optional parameter pointing to a url redirect post login
	 *
	 * @function
	 * @see useGuestLogin in comeon/guest-login
	 */
	const initializeLogin = useCallback(
		({ options, from } = {}) => {
			Cookies.set('postAuthLocation', from);
			if (isTrustlyLoginFlowEnabled) {
				redirectToExternalLogin({ options, from });
				return;
			}
			redirectToLogin({ options, from });
		},
		[redirectToLogin, redirectToExternalLogin, isTrustlyLoginFlowEnabled]
	);

	const initializeDepositLogin = useCallback(
		() => (isDepositFlowEnabled ? redirectToGuestLogin() : initializeLogin()),
		[initializeLogin, isDepositFlowEnabled, redirectToGuestLogin]
	);

	return { initializeLogin, initializeDepositLogin };
};

export default useLoginFlow;
